import React, { useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function ReviewsCarousel({ heading, items = [] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const timer = useRef(null);

  useEffect(() => {
    if (!emblaApi) return;
    timer.current = setInterval(() => emblaApi.scrollNext(), 6000);
    return () => clearInterval(timer.current);
  }, [emblaApi]);

  if (!items.length) return null;

  return (
    <section className="section-pad bg-[#0B1D3A] relative overflow-hidden" data-testid="reviews-section">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 0, transparent 40%), radial-gradient(circle at 80% 70%, #D4AF37 0, transparent 40%)" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-3">Traveler Stories</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-amber-50 font-medium">{heading}</h2>
            <div className="gold-divider mt-5" />
          </div>
          <div className="hidden sm:flex gap-2">
            <button data-testid="reviews-prev-button" onClick={() => emblaApi?.scrollPrev()} aria-label="Previous reviews" className="p-3 rounded-full border border-amber-500/30 text-amber-200 hover:bg-white/5 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="reviews-next-button" onClick={() => emblaApi?.scrollNext()} aria-label="Next reviews" className="p-3 rounded-full border border-amber-500/30 text-amber-200 hover:bg-white/5 transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex gap-5">
            {items.map((r, i) => (
              <figure key={i} data-testid={`review-card-${i}`} className="min-w-[86%] sm:min-w-[47%] lg:min-w-[31.8%] bg-[#0D2240]/80 backdrop-blur border border-amber-500/20 rounded-2xl p-7 flex flex-col select-none">
                <Quote className="w-7 h-7 text-amber-400/60 mb-4" aria-hidden="true" />
                <blockquote className="text-slate-200 leading-relaxed text-[15px] flex-1">"{r.quote}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-amber-500/15">
                  <div className="flex gap-1 mb-2" aria-label={`${r.rating || 5} out of 5 stars`}>
                    {Array.from({ length: r.rating || 5 }).map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" aria-hidden="true" />
                    ))}
                  </div>
                  <p className="text-amber-100 font-semibold text-sm">{r.name}</p>
                  <p className="text-slate-400 text-xs mt-0.5 tracking-wide uppercase">{r.trip}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <p className="text-center text-slate-500 text-xs mt-6 sm:hidden">Swipe to read more</p>
      </div>
    </section>
  );
}
